"use client";

import { useEffect } from "react";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useSession } from "@/contexts/sessionContext";
import { useAnalyticsEvents } from "@/hooks/useAnalyticsEvents";
import { useStore } from "@/hooks/useStore";
import { GROVER_BASE_URL } from "@/modules/config";
import { setDatadogTrackingConsent } from "@/modules/lib/observability/datadogUtils";
import { useLocale, useTranslations } from "next-intl";
import * as CookieConsent from "vanilla-cookieconsent";
import { CookieConsentStyles } from "./styles";

export const CookieModal: React.FC = () => {
  const t = useTranslations();
  const locale = useLocale();
  const { user } = useSession();
  const { enrichAnalyticsUser } = useAnalyticsEvents();

  const { correlatedMainstore: store } = useStore();

  useEffect(() => {
    CookieConsent.run({
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: true,
        },
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: t.raw("cookies.modal.title"),
              description: t.markup("cookies.modal.description", {
                "link-cookie-policy": (chunks) =>
                  `<a href="${GROVER_BASE_URL}/${store}-${locale}${t.raw("link.href.cookie_policy")}">${chunks}</a>`,
                "link-privacy-policy": (chunks) =>
                  `<a href="${GROVER_BASE_URL}/${store}-${locale}${t.raw("link.href.privacy_policy")}">${chunks}</a>`,
              }),
              acceptAllBtn: t.raw("cookies.modal.accept"),
              acceptNecessaryBtn: t.raw("cookies.modal.necessary"),
              showPreferencesBtn: t.raw("cookies.modal.settings"),
            },
            preferencesModal: {
              title: t.raw("cookies.preferences.preference_title"),
              savePreferencesBtn: t.raw("cookies.preferences.save"),
              closeIconLabel: t.raw("cookies.preferences.close"),
              sections: [
                {
                  description: t.markup(
                    "cookies.preferences.preference_description",
                    {
                      "link-cookie-policy": (chunks) =>
                        `<a href="${GROVER_BASE_URL}/${store}-${locale}${t.raw("link.href.cookie_policy")}">${chunks}</a>`,
                      "link-privacy-policy": (chunks) =>
                        `<a href="${GROVER_BASE_URL}/${store}-${locale}${t.raw("link.href.privacy_policy")}">${chunks}</a>`,
                    },
                  ),
                },
                {
                  title: t.raw("cookies.preferences.strictly_necessary"),
                  description: t.raw(
                    "cookies.preferences.strictly_necessary_description",
                  ),
                  linkedCategory: "necessary",
                },
                {
                  title: t.raw("cookies.preferences.performance_analytics"),
                  description: t.raw(
                    "cookies.preferences.performance_analytics_description",
                  ),
                  linkedCategory: "analytics",
                },
              ],
            },
          },
        },
      },
      guiOptions: {
        consentModal: {
          layout: "box",
          position: "bottom left",
          equalWeightButtons: false,
        },
        preferencesModal: {
          layout: "bar",
          position: "left",
          equalWeightButtons: false,
        },
      },
      onConsent: () => {
        const { acceptedCategories } = CookieConsent.getUserPreferences();

        if (acceptedCategories.includes("analytics")) {
          if (user) {
            enrichAnalyticsUser(user);
          }

          setDatadogTrackingConsent("granted");
        }
      },
    });
  }, [t, user, store, locale, enrichAnalyticsUser]);

  return <CookieConsentStyles />;
};
